// SmartSuggest Sidebar Component
// AI-powered failure mode suggestions for PFMEA Editor
// ============================================================

import React, { useState } from 'react';
import {
    Sparkles,
    Zap,
    Lightbulb,
    ChevronDown,
    ChevronUp,
    Plus,
    Check,
    RefreshCw,
    AlertTriangle,
    X,
} from 'lucide-react';
import clsx from 'clsx';
import {
    getSmartSuggestions,
    FailureModeSuggestion,
    SmartSuggestRequest,
} from '../services/pfmeaSmartSuggest';
import * as knowledgeBankService from '../services/knowledgeBankService';
import { useAppContext } from '../contexts/AppContext';
import { Bookmark, BookmarkCheck } from 'lucide-react';

interface SmartSuggestPanelProps {
    processStepName: string;
    processStepNumber?: string;
    processFunction?: string;
    industry?: string;
    existingFailureModes?: string[];
    existingPreventionControls?: string[];
    existingDetectionControls?: string[];
    onAddSuggestion: (suggestion: FailureModeSuggestion) => void;
    onOpenDraftQueue?: () => void;
    onClose?: () => void;
    isDark?: boolean;
}

const SmartSuggestPanel: React.FC<SmartSuggestPanelProps> = ({
    processStepName,
    processStepNumber,
    processFunction,
    industry,
    existingFailureModes,
    existingPreventionControls: _existingPreventionControls = [],
    existingDetectionControls: _existingDetectionControls = [],
    onAddSuggestion,
    onOpenDraftQueue: _onOpenDraftQueue,
    onClose,
    isDark = false,
}) => {
    const [suggestions, setSuggestions] = useState<FailureModeSuggestion[]>([]);
    const [analysisNotes, setAnalysisNotes] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [addedIndices, setAddedIndices] = useState<Set<number>>(new Set());
    const [savedToBankIndices, setSavedToBankIndices] = useState<Set<number>>(new Set());
    const [isSavingToBank, setIsSavingToBank] = useState<number | null>(null);

    const { activeProject, standardConfig } = useAppContext();

    const handleGenerate = async () => {
        setIsLoading(true);
        setError(null);
        setSuggestions([]);

        try {
            const request: SmartSuggestRequest = {
                processStepName,
                processStepNumber,
                processFunction,
                industry,
                existingFailureModes,
            };

            const result = await getSmartSuggestions(request);
            setSuggestions(result.suggestions || []);
            setAnalysisNotes(result.analysisNotes || '');
        } catch (err: unknown) {
            console.error('SmartSuggest error:', err);
            const message = err instanceof Error ? err.message : 'Failed to generate suggestions';
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddSuggestion = (suggestion: FailureModeSuggestion, index: number) => {
        onAddSuggestion(suggestion);
        setAddedIndices(prev => new Set([...prev, index]));

        // If this item was previously pulled from the knowledge bank, we should increment usage
        // (In future implementation, we'd track the KB-ID here)
    };

    const handleSaveToBank = async (suggestion: FailureModeSuggestion, index: number) => {
        setIsSavingToBank(index);
        try {
            const metadata = {
                processType: processStepName,
                industry: industry,
                fmeaStandard: standardConfig?.name || 'AIAG_VDA',
                sourceProject: activeProject || 'Generic',
            };

            const items = knowledgeBankService.aiSuggestionToKnowledgeItem({
                failureMode: suggestion.failureMode,
                effect: suggestion.failureEffect,
                cause: suggestion.failureCause,
                category: suggestion.processElement,
                severity: suggestion.severity,
                occurrence: suggestion.occurrence,
                detection: suggestion.detection,
                controlPrevention: suggestion.preventionControls?.[0], // Taking the primary control
                controlDetection: suggestion.detectionControls?.[0],
            }, metadata);

            // Save the failure mode item (primary)
            if (items.length > 0) {
                await knowledgeBankService.createKnowledgeItem(items[0]);
            }

            setSavedToBankIndices(prev => new Set([...prev, index]));
        } catch (err) {
            console.error('Failed to save to knowledge bank:', err);
        } finally {
            setIsSavingToBank(null);
        }
    };

    const get4MColor = (element: string) => {
        const colors: Record<string, string> = {
            'Man': 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
            'Machine': 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
            'Material': 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
            'Method': 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
        };
        return colors[element] || 'bg-gray-100 text-gray-700';
    };

    const getAPColor = (ap: string) => {
        const colors: Record<string, string> = {
            'H': 'bg-red-500 text-white',
            'M': 'bg-yellow-500 text-black',
            'L': 'bg-green-500 text-white',
        };
        return colors[ap] || 'bg-gray-500 text-white';
    };

    return (
        <div className={clsx(
            'w-96 h-full flex flex-col border-l overflow-hidden',
            isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
        )}>
            {/* Header */}
            <div className={clsx(
                'p-4 border-b flex items-center justify-between',
                isDark ? 'border-slate-700' : 'border-slate-200'
            )}>
                <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
                        <Sparkles size={20} className="text-white" />
                    </div>
                    <div>
                        <h3 className={clsx('font-semibold', isDark ? 'text-white' : 'text-slate-800')}>
                            SmartSuggest™
                        </h3>
                        <p className="text-xs text-slate-500">AI-Powered Failure Analysis</p>
                    </div>
                </div>
                {onClose && (
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <X size={20} />
                    </button>
                )}
            </div>

            {/* Process Step Info */}
            <div className={clsx('p-4 border-b', isDark ? 'border-slate-700' : 'border-slate-200')}>
                <p className="text-xs text-slate-500 mb-1">Analyzing Process Step:</p>
                <p className={clsx('font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                    {processStepNumber && <span className="text-purple-500">{processStepNumber}: </span>}
                    {processStepName}
                </p>
                {processFunction && (
                    <p className="text-sm text-slate-500 mt-1">{processFunction}</p>
                )}
            </div>

            {/* Generate Button */}
            <div className="p-4">
                <button
                    onClick={handleGenerate}
                    disabled={isLoading || !processStepName}
                    className={clsx(
                        'w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all',
                        isLoading || !processStepName
                            ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90 shadow-lg'
                    )}
                >
                    {isLoading ? (
                        <>
                            <RefreshCw size={18} className="animate-spin" />
                            Analyzing...
                        </>
                    ) : (
                        <>
                            <Zap size={18} />
                            Generate Failure Modes
                        </>
                    )}
                </button>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mx-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                        <AlertTriangle size={16} />
                        <span className="text-sm">{error}</span>
                    </div>
                </div>
            )}

            {/* Suggestions List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {suggestions.length === 0 && !isLoading && !error && (
                    <div className="text-center py-8">
                        <Lightbulb size={40} className="mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500 text-sm">
                            Click "Generate Failure Modes" to get AI-powered suggestions based on this process step.
                        </p>
                    </div>
                )}

                {suggestions.map((suggestion, index) => (
                    <div
                        key={index}
                        className={clsx(
                            'rounded-xl border overflow-hidden transition-all',
                            isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200',
                            addedIndices.has(index) && 'opacity-50'
                        )}
                    >
                        {/* Suggestion Header */}
                        <div
                            className="p-3 cursor-pointer"
                            onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium', get4MColor(suggestion.processElement))}>
                                            {suggestion.processElement}
                                        </span>
                                        <span className={clsx('text-xs px-2 py-0.5 rounded-full font-bold', getAPColor(suggestion.actionPriority))}>
                                            AP: {suggestion.actionPriority}
                                        </span>
                                    </div>
                                    <p className={clsx('font-medium text-sm', isDark ? 'text-white' : 'text-slate-800')}>
                                        {suggestion.failureMode}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1">
                                    {expandedIndex === index ? (
                                        <ChevronUp size={16} className="text-slate-400" />
                                    ) : (
                                        <ChevronDown size={16} className="text-slate-400" />
                                    )}
                                </div>
                            </div>

                            {/* S/O/D Ratings */}
                            <div className="flex items-center gap-3 mt-2 text-xs">
                                <span className="text-red-500 font-medium">S: {suggestion.severity}</span>
                                <span className="text-amber-500 font-medium">O: {suggestion.occurrence}</span>
                                <span className="text-blue-500 font-medium">D: {suggestion.detection}</span>
                            </div>
                        </div>

                        {/* Expanded Details */}
                        {expandedIndex === index && (
                            <div className={clsx('p-3 border-t', isDark ? 'border-slate-600' : 'border-slate-200')}>
                                <div className="space-y-3 text-sm">
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Effect:</p>
                                        <p className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                                            {suggestion.failureEffect}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Cause:</p>
                                        <p className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                                            {suggestion.failureCause}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Prevention Controls:</p>
                                        <ul className="list-disc list-inside">
                                            {suggestion.preventionControls?.map((ctrl, i) => (
                                                <li key={i} className={isDark ? 'text-slate-300' : 'text-slate-700'}>{ctrl}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Detection Controls:</p>
                                        <ul className="list-disc list-inside">
                                            {suggestion.detectionControls?.map((ctrl, i) => (
                                                <li key={i} className={isDark ? 'text-slate-300' : 'text-slate-700'}>{ctrl}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    {suggestion.rationale && (
                                        <div>
                                            <p className="text-xs text-slate-500 mb-1">Rationale:</p>
                                            <p className={clsx('text-xs italic', isDark ? 'text-slate-400' : 'text-slate-500')}>
                                                {suggestion.rationale}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2 mt-4">
                                    <button
                                        onClick={() => handleAddSuggestion(suggestion, index)}
                                        disabled={addedIndices.has(index)}
                                        className={clsx(
                                            'flex-1 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all',
                                            addedIndices.has(index)
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-purple-500 text-white hover:bg-purple-600'
                                        )}
                                    >
                                        {addedIndices.has(index) ? (
                                            <>
                                                <Check size={16} />
                                                Added to PFMEA
                                            </>
                                        ) : (
                                            <>
                                                <Plus size={16} />
                                                Add to PFMEA
                                            </>
                                        )}
                                    </button>

                                    <button
                                        onClick={() => handleSaveToBank(suggestion, index)}
                                        disabled={savedToBankIndices.has(index) || isSavingToBank === index}
                                        className={clsx(
                                            'flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all',
                                            savedToBankIndices.has(index)
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                                        )}
                                        title="Save to Knowledge Bank for future AI learning"
                                    >
                                        {isSavingToBank === index ? (
                                            <RefreshCw size={16} className="animate-spin" />
                                        ) : savedToBankIndices.has(index) ? (
                                            <>
                                                <BookmarkCheck size={16} />
                                                Saved
                                            </>
                                        ) : (
                                            <>
                                                <Bookmark size={16} />
                                                Save to Library
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Analysis Notes */}
            {analysisNotes && (
                <div className={clsx(
                    'p-4 border-t',
                    isDark ? 'border-slate-700 bg-slate-700/30' : 'border-slate-200 bg-slate-50'
                )}>
                    <p className="text-xs text-slate-500 mb-1">AI Analysis Notes:</p>
                    <p className={clsx('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>
                        {analysisNotes}
                    </p>
                </div>
            )}
        </div>
    );
};

export default SmartSuggestPanel;
